import moment from 'moment'
import { ascending } from 'd3-array'

import { listService } from '../../common/services/list.service'
import { SUBGROUP_COLUMN } from './report-data-set.constants'
import { PAGE_CONTROL_BAR_KEY } from '../constants'

class ReportDataSetService {
  /**
   * Get a list of data sets
   */
  getDataSetOptions (page, chartType, { includeUntyped = true } = {}) {
    if (!page.parent || !page.parent.dataSets) {
      return []
    }
    // Exclude containerId which is not a data set
    const { containerId, ...dataSets } = page.parent.dataSets
    return Object.keys(dataSets)
      .filter((key) => {
        // Filter out datasets that have a list of chart types that does not include the one specified
        const chartTypes = this.getDataSetChartTypes(dataSets[key])
        return (includeUntyped && chartTypes.length === 0) || chartTypes.includes(chartType)
      })
      .map((key) => {
        return {
          value: key,
          label: dataSets[key].label || key
        }
      })
      .sort((a, b) => ascending(a.label, b.label))
  }

  /**
   * Get a list of chart types for a dataset either explicitly specified or inferred from data types
   */
  getDataSetChartTypes (dataSet) {
    // Return chart types if explicitly specified or if there are no data fields to infer a type from
    if (dataSet.chartTypes || !dataSet.dataFields || !dataSet.dataFields.length > 0) {
      return dataSet.chartTypes || []
    }

    // Infer chart types based on data fields if no type is explicitly specified
    const chartTypes = []
    if (dataSet.dataFields.includes('row') && dataSet.dataFields.includes('column')) {
      chartTypes.push('table')
    }
    if (dataSet.dataFields.includes('states') && dataSet.dataFields.includes('stages')) {
      chartTypes.push('sankey')
    }
    if (dataSet.dataFields.includes('types') && dataSet.dataFields.includes('data')) {
      chartTypes.push('sunburst')
    }

    return chartTypes
  }

  /**
   * Get the data set metadata from the given page's report
   */
  getDataSet (page, dataKey) {
    if (dataKey && page.parent && page.parent.dataSets) {
      return page.parent.dataSets[dataKey]
    }
  }

  /**
   * Get a list of data fields available for a data set
   */
  getDataFieldOptions (page, dataKey) {
    const dataSet = this.getDataSet(page, dataKey) || {}
    return (dataSet.dataFields || []).map((key) => ({ value: key, label: key }))
  }

  /**
   * Get a list of subgroups available for a data set along with distinct values for each subgroup
   */
  getSubgroupsFromDataSet (page, dataKey) {
    const dataSet = this.getDataSet(page, dataKey) || {}
    return (dataSet.subgroups || []).map((key) => ({
      value: key,
      label: key,
      values: this.getSubgroupValues(page, dataKey, key)
    }))
  }

  /**
   * Get a list of distinct subgroup values for a given subgroup
   */
  getSubgroupValues (page, dataKey, subgroup) {
    if (!dataKey) {
      return []
    }
    return ((page.parent.dataSets[dataKey] || {}).subgroupValues || {})[subgroup] || []
  }

  /**
   * Get a list of distinct data field values for a given data field
   */
  getDataFieldValues (page, dataKey, dataField) {
    if (!dataKey) {
      return []
    }
    return ((page.parent.dataSets[dataKey] || {}).dataFieldValues || {})[dataField] || []
  }

  /**
   * Get a list of subgroups for display in a checkbox grid
   */
  getSubgroupGridOptions (page, block, columns = []) {
    const dataKey = block.dataKey
    if (!dataKey) {
      return []
    }

    const gridOptions = []
    const shouldIncludeOverall = columns.includes(SUBGROUP_COLUMN.STRATA)
    // Default the Overall row to the beginning
    const defaultBlockSubgroups = shouldIncludeOverall ? [{ value: null, label: 'Overall' }] : []
    const blockSubgroups = block.subgroups || defaultBlockSubgroups
    const selections = this.getSubgroupSelections(block)
    const dataSetSubgroups = [ ...this.getSubgroupsFromDataSet(page, dataKey) ]

    if (shouldIncludeOverall) {
      // Prepend an implied "Overall"/null subgroup to the dataset list
      dataSetSubgroups.unshift({ value: null, label: 'Overall', values: [] })
    }

    const checkIfRequired = (values) => values && values.length > 0 && !values.includes(null)

    // Start by adding the distinct rows from the selected subgroups (from strata & filter definitions)
    selections.forEach(({ row, rowLabel }) => {
      if (!gridOptions.find(({ value }) => value === row)) {
        const subgroup = { value: row, label: rowLabel }

        const dataSetSubgroup = dataSetSubgroups.find(({ value }) => value === row)
        if (dataSetSubgroup) {
          // A subgroup is required to have a selection (strata or filter) if it does not have a null value
          subgroup.isRequired = checkIfRequired(dataSetSubgroup.values)
        } else {
          // If the selected subgroup is not in the dataset flag as a warning
          subgroup.isWarning = true
        }
        gridOptions.push(subgroup)
      }
    })

    // Then add any subgroups from the data set that are not selected
    dataSetSubgroups.forEach((s) => {
      if (!gridOptions.find(({ value }) => value === s.value)) {
        const { values, ...subgroup } = s
        // If there is a label specified in the block config for this subgroup, use that label
        const subgroupFromBlockConfig = blockSubgroups.find(({ value }) => value === s.value)
        if (subgroupFromBlockConfig) {
          subgroup.label = subgroupFromBlockConfig.label
        }
        // A subgroup is required to have a selection (strata or filter) if it does not have a null value
        subgroup.isRequired = checkIfRequired(values)
        gridOptions.push(subgroup)
      }
    })

    // Then sort based on the subgroup order in the block config if an order has been saved
    if (blockSubgroups.length > 0) {
      listService.sortObjectList(gridOptions, 'value', blockSubgroups.map(({ value }) => value))
    }

    // Only allow selecting strata & filter together - all other combos are not allowed
    const allowMulti = [SUBGROUP_COLUMN.STRATA, SUBGROUP_COLUMN.FILTERS]
    return gridOptions.map((result) => {
      const option = { ...result, allowMulti }
      if (option.value === null) {
        option.disabledColumns = [SUBGROUP_COLUMN.PRIMARY, SUBGROUP_COLUMN.FILTERS, SUBGROUP_COLUMN.SLIDER, SUBGROUP_COLUMN.PAGE_FILTER, SUBGROUP_COLUMN.SPLIT_FILTER]
      }
      return option
    })
  }

  /**
   * Update a block's subgroup order/label list and sort/relabel the strata & filter subgroup definitions
   */
  updateBlockFromSubgroupGridOptions (block, subgroupGridOptions) {
    block.subgroups = subgroupGridOptions.map(({ value, label }) => ({ value, label }))
    const subgroupOrder = subgroupGridOptions.map(({ value }) => value)

    // sort and update labels of strata options
    const strataOptions = this.getStrataDefinitionOptions(block)
    if (strataOptions) {
      listService.sortObjectList(strataOptions, 'value', subgroupOrder)
      strataOptions.forEach((option) => {
        const subgroupGridOption = subgroupGridOptions.find(({ value }) => value === option.value)
        if (subgroupGridOption) {
          option.label = subgroupGridOption.label
        }
      })
    }

    // sort and update labels of filter fields
    const filterFields = this.getFilterDefinitionFields(block)
    if (filterFields) {
      listService.sortObjectList(filterFields, 'key', subgroupOrder)
      filterFields.forEach((field) => {
        const subgroupGridOption = subgroupGridOptions.find(({ value }) => value === field.key)
        if (subgroupGridOption) {
          field.label = subgroupGridOption.label
        }
      })
    }

    // update the slider label
    const sliderKey = this.getSliderDefinitionKey(block)
    if (sliderKey !== undefined) {
      const sliderSubgroup = subgroupGridOptions.find(({ value }) => value === sliderKey)
      if (sliderSubgroup) {
        block.props.sliderDefinition.label = sliderSubgroup.label
      }
    }

    return block
  }

  /**
   * Get a combined list of strata & filter selections for passing into the checkbox grid
   */
  getSubgroupSelections (block) {
    const currentSelections = []

    const strataOptions = this.getStrataDefinitionOptions(block) || []
    strataOptions.forEach(({ value, label }) => {
      currentSelections.push({ row: value, rowLabel: label, column: SUBGROUP_COLUMN.STRATA })
    })

    const filterFields = this.getFilterDefinitionFields(block) || []
    filterFields.forEach(({ key, label, isPrimary, isSplitChartFilter }) => {
      currentSelections.push({
        row: key,
        rowLabel: label,
        column: (() => {
          if (isPrimary) {
            return SUBGROUP_COLUMN.PRIMARY
          } else if (isSplitChartFilter) {
            return SUBGROUP_COLUMN.SPLIT_FILTER
          } else {
            return SUBGROUP_COLUMN.FILTERS
          }
        })()
      })
    })

    const sliderDef = block.props.sliderDefinition
    if (sliderDef !== undefined) {
      currentSelections.push({
        row: sliderDef.key,
        rowLabel: sliderDef.label,
        column: SUBGROUP_COLUMN.SLIDER
      })
    }

    const splitFilterDef = block.props.splitFilterDefinition
    if (splitFilterDef !== undefined) {
      currentSelections.push({
        row: splitFilterDef.key,
        rowLabel: splitFilterDef.label,
        column: SUBGROUP_COLUMN.SPLIT_FILTER
      })
    }

    const parentControlBar = block.props.parentControlBar
    if (parentControlBar !== undefined && parentControlBar.key === PAGE_CONTROL_BAR_KEY) {
      (parentControlBar.filters || []).forEach((filter) => {
        currentSelections.push({
          row: filter,
          rowLabel: filter,
          column: SUBGROUP_COLUMN.PAGE_FILTER
        })
      })
    }

    return currentSelections
  }

  /**
   * Update the block based on selections made in the checkbox grid
   */
  updateStrataAndFilterDefinitions (page, block, selections, subgroupLabels, removedCache = { strata: {}, filters: {}, primary: {}, splitChartFilter: {} }) {
    // Pull out existing strata/filter
    block.props.strataDefinition = block.props.strataDefinition || {}
    block.props.filterDefinition = block.props.filterDefinition || {}
    const strata = block.props.strataDefinition.options || []
    const filters = block.props.filterDefinition.fields || []

    const updated = {
      strata: [],
      filters: []
    }

    // Pull out the strata still selected
    strata.forEach((s) => {
      const isSelected = !!selections.find(({ row, column }) => {
        return row === s.value && column === SUBGROUP_COLUMN.STRATA
      })
      if (isSelected) {
        updated.strata.push(s)
      } else {
        removedCache.strata[s.value] = s
      }
    })

    // Pull out the filters still selected
    filters.forEach((f) => {
      const isSelected = !!selections.find(({ row, column }) => {
        return (
          (row === f.key && f.isPrimary && column === SUBGROUP_COLUMN.PRIMARY) ||
          (row === f.key && f.isSplitChartFilter && column === SUBGROUP_COLUMN.SPLIT_FILTER) ||
          (row === f.key && !f.isPrimary && !f.isSplitChartFilter && column === SUBGROUP_COLUMN.FILTERS)
        )
      })
      if (isSelected) {
        updated.filters.push(f)
      } else if (f.isPrimary) {
        removedCache.primary[f.key] = f
      } else if (f.isSplitChartFilter) {
        removedCache.splitChartFilter[f.key] = f
      } else {
        removedCache.filters[f.key] = f
      }
    })

    // Add new strata/filters
    selections.forEach(({ column, row }) => {
      const subgroup = subgroupLabels.find(({ value }) => value === row) || {}
      if (column === SUBGROUP_COLUMN.STRATA) {
        // push the strata option if it's new
        if (!updated.strata.find(({ value }) => value === row)) {
          const strata = removedCache.strata[row] || {
            value: subgroup.value || row,
            label: subgroup.label || row,
            legend: this.getStratificationValueOptions(page, block.dataKey, row)
          }
          updated.strata.push(strata)
        }
      } else if ([SUBGROUP_COLUMN.PRIMARY, SUBGROUP_COLUMN.FILTERS, SUBGROUP_COLUMN.SPLIT_FILTER].includes(column)) {
        const isSelectionPrimary = column === SUBGROUP_COLUMN.PRIMARY
        const isSelectionSplitFilter = column === SUBGROUP_COLUMN.SPLIT_FILTER

        // push the filter field if it's new
        if (!updated.filters.find(({ key, isPrimary, isSplitChartFilter }) => row === key && !!isPrimary === isSelectionPrimary && !!isSplitChartFilter === isSelectionSplitFilter)) {
          let filter
          if (isSelectionPrimary) {
            filter = removedCache.primary[row]
          } else if (isSelectionSplitFilter) {
            filter = removedCache.splitChartFilter[row]
          } else {
            filter = removedCache.filters[row]
          }

          if (!filter) {
            filter = {
              key: subgroup.value || row,
              label: subgroup.label || row,
              options: this.getSubgroupValueOptions(page, block.dataKey, row)
            }
            if (isSelectionPrimary) {
              filter.isPrimary = true
            } else if (isSelectionSplitFilter) {
              filter.isSplitChartFilter = true
            }
          }
          updated.filters.push(filter)
        }
      }
    })

    block.props.strataDefinition.options = updated.strata
    block.props.filterDefinition.fields = updated.filters

    return { updatedBlock: block, removedCache }
  }

  /**
   * Get a list of values/labels for a given subgroup
   */
  getSubgroupValueOptions (page, dataKey, subgroup, nullLabel = 'All') {
    const values = this.getSubgroupValues(page, dataKey, subgroup)
    return values.map((value) => ({
      value,
      label: (value === null) ? nullLabel : value
    }))
  }

  /**
   * Get a list of value/labels for a given stratification
   */
  getStratificationValueOptions (page, dataKey, strataSubgroup) {
    const values = this.getSubgroupValues(page, dataKey, strataSubgroup)
    return values
      .filter((value) => (value !== null))
      .map((value) => ({
        value,
        label: value
      }))
  }

  /**
   * Pull out the strata definition options if they exist
   */
  getStrataDefinitionOptions (block) {
    if (block &&
      block.props &&
      block.props.strataDefinition
    ) {
      return block.props.strataDefinition.options
    }
  }

  /**
   * Pull out the filter definition fields if they exist
   */
  getFilterDefinitionFields (block) {
    if (block &&
      block.props &&
      block.props.filterDefinition
    ) {
      return block.props.filterDefinition.fields
    }
  }

  getSliderDefinitionKey (block) {
    return block.props.sliderDefinition && block.props.sliderDefinition.key
  }

  getSplitChartFilter (block) {
    if (!block || !block.props || !block.props.filterDefinition || !Array.isArray(block.props.filterDefinition.fields)) {
      return
    }

    return block.props.filterDefinition.fields.find(({ isSplitChartFilter }) => isSplitChartFilter)
  }

  getMappedSplitChartSelections (splitChartFilter) {
    if (splitChartFilter) {
      const selections = {
        key: splitChartFilter.key,
        values: splitChartFilter.defaultSelections || [null, null]
      }
      const validOptions = splitChartFilter.options.filter((option) => !option.hide)
      selections.values.forEach((value, i) => {
        if (!validOptions.find((option) => option.value === value)) {
          selections[i] = splitChartFilter.options.find((option) => option.value === null) ? null : splitChartFilter.options[0].value
        }
      })
      return {
        key: splitChartFilter.key,
        values: splitChartFilter.defaultSelections || [null, null]
      }
    }
  }

  getSplitChartFilterSelections (block) {
    const splitChartFilter = this.getSplitChartFilter(block)
    return this.getMappedSplitChartSelections(splitChartFilter)
  }

  getFilterDefinitionSplitChartSelections (filterDefinition) {
    const splitChartFilter = filterDefinition && filterDefinition.fields && filterDefinition.fields.find((field) => field.isSplitChartFilter)
    return this.getMappedSplitChartSelections(splitChartFilter)
  }

  /**
   * Map from strata definition options form into form expected by the CategoryListEdit component.
   */
  getStrataLegendLabels (block) {
    const strataOptions = this.getStrataDefinitionOptions(block) || []
    return strataOptions.map((option) => {
      const newOption = { ...option, values: option.legend }
      delete newOption.legend
      return newOption
    })
  }

  /**
   * Map back into the strata definition options form from the CategoryListEdit form and update the block
   */
  updateStrataLegendLabels (block, legendLabels) {
    block.props.strataDefinition = block.props.strataDefinition ? { ...block.props.strataDefinition } : {}
    // map "values" back into "legend"
    block.props.strataDefinition.options = legendLabels.map((x) => {
      x = { ...x }
      x.legend = x.values
      delete x.values
      return x
    })
    return block
  }

  /**
   * Map from filter definition fields form into form expected by the CategoryListEdit component.
   */
  getFilterOptionLabels (block) {
    const filterFields = this.getFilterDefinitionFields(block) || []
    return filterFields.map((option) => {
      const newOption = { ...option, value: option.key, values: option.options }
      delete newOption.options
      return newOption
    })
  }

  /**
   * Map back into the filter definition fields form from the CategoryListEdit form and update the block
   */
  updateFilterOptionLabels (block, optionLabels) {
    block.props.filterDefinition = block.props.filterDefinition ? { ...block.props.filterDefinition } : {}
    // map "values" back into "options"
    block.props.filterDefinition.fields = optionLabels.map((x) => {
      x = { ...x }
      x.options = x.values
      delete x.value // same as key
      delete x.values
      return x
    })
    return block
  }

  buildSliderDefinition (key, label, distinctValues) {
    let sliderDefinition = { key, label }
    let values = distinctValues.filter((value) => value !== null)
    const areDates = values.length > 0 && values.every((value) => this.isDateString(value))

    if (areDates) {
      const dateSpecificOptions = this.buildDateSliderOptions(values)
      sliderDefinition = {
        ...sliderDefinition,
        ...dateSpecificOptions
      }
    } else {
      const options = values.sort(ascending).map((value) => ({ value: parseInt(value), label: value }))
      sliderDefinition = { ...sliderDefinition, options }
    }
    return sliderDefinition
  }

  isDateString (value) {
    const acceptedDateFormats = [
      'YYYY-MM-DDTHH:mm:ss.SSSZ',
      'YYYY-MM-DDTHH:mm:ss',
      'YYYY-MM-DD HH:mm:ss',
      'YYYY-MM-DD'
    ]
    for (let format of acceptedDateFormats) {
      let potentialDate = moment.utc(value, format, true)
      if (potentialDate.isValid()) {
        return true
      }
    }

    return false
  }

  buildDateSliderOptions (values) {
    values = [ ...values ].sort(ascending)

    let dateValueFormat = 'YYYY'
    if (values.length > 1) {
      // figure out the diff between the first two to determine label format (month or year)
      const date1 = moment.utc(values[0])
      const date2 = moment.utc(values[1])
      const yearDiff = date2.diff(date1, 'years')
      if (yearDiff < 1) {
        dateValueFormat = 'MMM YYYY'
      }
    }

    const options = values.map((value) => ({ value, label: moment.utc(value).format(dateValueFormat) }))

    return { dateMode: true, dateValueFormat, options }
  }

  getDataSetLabels (page, dataKey) {
    const dataSet = this.getDataSet(page, dataKey) || {}
    return dataSet.dataLabels || {}
  }
}

export const reportDataSetService = new ReportDataSetService()
